/**
 * vmWakeBannerPolicy — the PURE rule behind the "waking your workstation"
 * strip: given the account's vm doc and the current time, may the band claim
 * a wake is in flight?
 *
 * Kept free of React and firebase so the rule is testable on its own;
 * VmLifecycleBanner.tsx holds the wiring and re-exports everything here.
 *
 * ── WHY THIS EXISTS (owner report 2026-08-31) ──────────────────────────────
 * An established account whose desktop was demonstrably live and working
 * still read "waking your workstation — ready in about a minute."
 *
 * `lifecycleState: 'waking'` has exactly ONE clearing writer in the whole
 * codebase (functions/src/prest0nVm/bootStatus.ts), and it only runs when
 * something actively polls the boot-status probe. A wake whose promotion
 * never ran — the poll stopped, the user navigated away, the function
 * errored — leaves that field at 'waking' FOREVER. The banner read the field
 * alone, with no freshness bound and no liveness evidence, so it replayed a
 * long-dead wake as live progress indefinitely.
 *
 * The server already knows better: it stamps the wake clock
 * (`wakeRequestedAt`, falling back to `lifecycleUpdatedAt`) on the very doc
 * the banner reads, and treats a wake past WORKSTATION_STALL_MINUTES as
 * stalled. So the band reads that same clock and simply stops claiming a
 * wake it can no longer vouch for. Past the bound the /desktop card — which
 * holds the server's authoritative verdict and a retry — owns the story; a
 * top strip promising "about a minute" does not.
 *
 * This also settles the "old vs newly onboarded accounts" half of the
 * directive: a legacy doc predating the lifecycle fields carries no wake
 * clock, and an UNCLOCKED 'waking' is treated as untrustworthy rather than
 * as a live wake — so old accounts stop inheriting a phantom banner, while a
 * genuine fresh wake on any account still shows exactly as before.
 */

/**
 * How long a wake may claim the band. Mirrors the server's own stall clock
 * (functions/src/prest0nVm/bootStatus.ts WORKSTATION_STALL_MINUTES = 30) so
 * client and server agree on when a wake has gone wrong instead of each
 * guessing from a local clock. Kept in sync deliberately — the agreement is
 * asserted in vmLifecycleBanner.test.ts.
 */
export const WORKSTATION_WAKE_STALE_MINUTES = 30;

/**
 * How long the "ready in about a minute" promise stays honest. Past it the
 * wake is still believed (under the bound above) but the copy stops quoting
 * an estimate it has already missed.
 */
export const WORKSTATION_WAKE_ESTIMATE_GRACE_MINUTES = 4;

/** Firestore Timestamp | Date | epoch-ms → epoch ms, or null when unusable. */
function toMillis(raw: unknown): number | null {
  if (raw == null) return null;
  const anyRaw = raw as any;
  if (typeof anyRaw?.toMillis === 'function') {
    try {
      const ms = anyRaw.toMillis();
      return typeof ms === 'number' && Number.isFinite(ms) ? ms : null;
    } catch {
      return null;
    }
  }
  if (anyRaw instanceof Date) {
    const ms = anyRaw.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof anyRaw === 'number' && Number.isFinite(anyRaw)) return anyRaw;
  return null;
}

/**
 * The wake clock the server stamps on this doc: the wake request itself,
 * falling back to the lifecycle write that set 'waking'. Null on a legacy
 * doc carrying neither — an unclocked wake, which callers must not trust.
 */
export function wakeStartedAtMs(vm: Record<string, any> | null): number | null {
  if (!vm) return null;
  return toMillis(vm.wakeRequestedAt) ?? toMillis(vm.lifecycleUpdatedAt);
}

/**
 * Is this vm doc a wake the band may still speak for? An unclocked or
 * long-expired 'waking' is NOT — the banner would be asserting live progress
 * it cannot vouch for.
 */
export function isWakeBannerEligible(
  vm: Record<string, any> | null,
  nowMs: number,
): boolean {
  if (!vm) return false;
  const lifecycle = typeof vm.lifecycleState === 'string' ? vm.lifecycleState : 'active';
  if (lifecycle !== 'waking') return false;
  if (vm.stopReason === 'billing') return false; // billing banners own that journey
  const startedAt = wakeStartedAtMs(vm);
  if (startedAt === null) return false; // unclocked legacy doc — never claim a wake
  // A clock in the future (skew) reads as "just started", never as stale.
  return nowMs - startedAt <= WORKSTATION_WAKE_STALE_MINUTES * 60_000;
}

/**
 * Has the wake outrun the estimate the copy quotes? Callers soften the
 * wording rather than keep promising a number already missed.
 */
export function isPastWakeEstimate(
  vm: Record<string, any> | null,
  nowMs: number,
): boolean {
  const startedAt = wakeStartedAtMs(vm);
  if (startedAt === null) return false;
  return nowMs - startedAt > WORKSTATION_WAKE_ESTIMATE_GRACE_MINUTES * 60_000;
}
